<template>
    <section class="flex min-w-0 flex-col gap-4">
        <div
            class="flex flex-col gap-4 rounded-xl border border-zinc-200 bg-white px-[18px] py-4 dark:border-gray-700 dark:bg-gray-800"
        >
            <div class="flex flex-wrap items-start gap-3.5">
                <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-zinc-100 text-sm font-semibold text-zinc-900 dark:bg-gray-700 dark:text-white"
                >
                    {{ initials }}
                </div>
                <div class="flex min-w-[200px] flex-1 flex-col gap-0.5">
                    <div class="flex flex-wrap items-center gap-2">
                        <span
                            class="text-[17px] font-semibold tracking-tight text-zinc-950 dark:text-white"
                        >
                            {{ cashout.user?.name }}
                        </span>
                        <span
                            class="rounded-full border border-zinc-200 px-2 py-px text-xs font-medium text-zinc-600 dark:border-gray-600 dark:text-gray-300"
                        >
                            {{ reviewStatusLabel }}
                        </span>
                        <span
                            v-if="isSkipped"
                            class="rounded-full bg-zinc-100 px-2 py-px text-xs font-medium text-zinc-600 dark:bg-gray-700 dark:text-gray-300"
                        >
                            Pulado
                        </span>
                    </div>
                    <div
                        class="flex flex-wrap gap-2 text-[13px] text-zinc-500 dark:text-gray-400"
                    >
                        <router-link
                            :to="{
                                name: 'finnancial.cashout-details',
                                params: { cashout_id: cashout.id },
                            }"
                            target="_blank"
                            class="font-mono hover:text-zinc-900 hover:underline dark:hover:text-white"
                        >
                            Saque #{{ cashout.id }}
                        </router-link>
                        <span>·</span>
                        <span>{{ cashout.user?.email }}</span>
                        <span>·</span>
                        <span>{{ cashout.created_at }}</span>
                    </div>
                </div>
                <router-link
                    :to="{
                        name: 'user.metrics',
                        params: { user_id: cashout.user_id },
                    }"
                    target="_blank"
                    class="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-[13px] font-medium text-zinc-950 hover:bg-zinc-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                >
                    Análise do seller
                    <ArrowUpRightIcon class="h-3.5 w-3.5" />
                </router-link>
            </div>

            <div
                class="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] overflow-hidden rounded-[10px] border border-zinc-200 dark:border-gray-700"
            >
                <div class="flex flex-col gap-1 px-3.5 py-3">
                    <span
                        class="text-[12.5px] text-zinc-500 dark:text-gray-400"
                    >
                        Valor solicitado
                    </span>
                    <span
                        class="whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight text-zinc-950 dark:text-white"
                    >
                        {{ $root.formatCurrency(cashout.amount) }}
                    </span>
                </div>
                <div
                    class="flex flex-col gap-1 px-3.5 py-3 shadow-[-1px_0_0_#e4e4e7] dark:shadow-[-1px_0_0_#374151]"
                >
                    <span
                        class="text-[12.5px] text-zinc-500 dark:text-gray-400"
                    >
                        Taxa aplicada
                    </span>
                    <span
                        class="whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight text-zinc-500 dark:text-gray-400"
                    >
                        {{ $root.formatCurrency(cashout.fee) }}
                    </span>
                </div>
                <div
                    class="flex flex-col gap-1 px-3.5 py-3 shadow-[-1px_0_0_#e4e4e7] dark:shadow-[-1px_0_0_#374151]"
                >
                    <span
                        class="text-[12.5px] text-zinc-500 dark:text-gray-400"
                    >
                        A receber
                    </span>
                    <span
                        class="whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight text-zinc-950 dark:text-white"
                    >
                        {{ $root.formatCurrency(cashout.net_value) }}
                    </span>
                </div>
            </div>

            <div
                v-show="tagsCount > 0 || userAudits.length > 0"
                class="flex flex-wrap items-center gap-2"
            >
                <cashout-tags
                    :key="cashout.id"
                    :cashout="cashout"
                    @loaded="tagsCount = $event"
                />
                <router-link
                    v-for="userAudit in userAudits"
                    :key="userAudit.id"
                    :to="{
                        name: 'audit.interactions',
                        params: { audit_id: userAudit.id },
                    }"
                    target="_blank"
                    class="flex h-[26px] items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 text-[12.5px] font-medium text-red-700 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400"
                >
                    <span class="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                    Auditoria
                    {{ userAudit.status === "closed" ? "finalizada" : "ativa" }}
                </router-link>
            </div>
        </div>

        <chargeback-risk-badge :key="cashout.id" :cashout-id="cashout.id" />

        <cashout-checklist
            ref="cashoutChecklist"
            :cashout-id="cashout.id"
            @loaded="$emit('checklist-loaded', $event)"
        />

        <seller-context-card
            :review="review"
            :payable-details="payableDetails"
            :is-same-day-urgent="isSameDayUrgent"
        />
    </section>
</template>

<script>
import { ArrowUpRightIcon } from "@heroicons/vue/24/outline";
import ChargebackRiskBadge from "./ChargebackRiskBadge.vue";
import CashoutChecklist from "./CashoutChecklist.vue";
import SellerContextCard from "./SellerContextCard.vue";
import CashoutTags from "../CashoutTags.vue";
import { getUserInitials } from "../../../utils/reviewHelpers.js";

const REVIEW_STATUS_LABELS = {
    pending: "Solicitado",
    warning: "Atenção",
    approved: "Aprovado",
    rejected: "Reprovado",
};

export default {
    name: "ReviewDecisionPanel",
    components: {
        ArrowUpRightIcon,
        ChargebackRiskBadge,
        CashoutChecklist,
        SellerContextCard,
        CashoutTags,
    },
    props: {
        review: {
            type: Object,
            required: true,
        },
        payableDetails: {
            type: Object,
            default: null,
        },
        isSameDayUrgent: {
            type: Boolean,
            default: false,
        },
        isSkipped: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["checklist-loaded"],
    data() {
        return {
            tagsCount: 0,
        };
    },
    computed: {
        cashout() {
            return this.review.cashout;
        },
        initials() {
            return getUserInitials(this.cashout?.user?.name);
        },
        reviewStatusLabel() {
            return (
                REVIEW_STATUS_LABELS[this.review.status] || this.review.status
            );
        },
        userAudits() {
            return this.payableDetails?.user_audits || [];
        },
    },
    watch: {
        "review.id"() {
            this.tagsCount = 0;
        },
    },
    methods: {
        async reloadCashoutChecklist() {
            return this.$refs.cashoutChecklist?.loadChecklist();
        },
    },
};
</script>
